import type { HeadlinePinPosition } from '../../types/campaign';

// Pinned Headlines spec §2.1: compact pin button shared between the
// Campaign editor and the row editor. Click cycles the pin position:
// unpinned → P1 → P2 → P3 → unpinned. Pure controlled component —
// current state in, onChange handler out — so the parent owns the
// headline array shape and the button stays a leaf.
//
// Multiple headlines can share the same position (Google's RSA
// rotates among the pinned candidates in that slot). No uniqueness
// validation; the button cycles the same way regardless of what
// other headlines are pinned to.

const CYCLE: HeadlinePinPosition[] = [null, 1, 2, 3];

export function nextPinPosition(current: HeadlinePinPosition): HeadlinePinPosition {
  const idx = CYCLE.indexOf(current);
  return CYCLE[(idx + 1) % CYCLE.length] ?? null;
}

// Inline pin glyph — no icon library dependency, matches the slate
// palette used elsewhere. Adapted from the lucide-react `Pin` outline.
function PinIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </svg>
  );
}

export type HeadlinePinButtonProps = {
  value: HeadlinePinPosition;
  onChange: (next: HeadlinePinPosition) => void;
  disabled?: boolean;
};

export function HeadlinePinButton({ value, onChange, disabled }: HeadlinePinButtonProps) {
  const pinned = value !== null;
  const tooltip = pinned
    ? `Pinned to position ${value}. Click to cycle pin position.`
    : 'Not pinned. Click to pin to position 1.';

  return (
    <button
      type="button"
      onClick={() => onChange(nextPinPosition(value))}
      disabled={disabled}
      title={tooltip}
      aria-label={tooltip}
      className={
        'inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ' +
        (pinned
          ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
          : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50')
      }
    >
      <PinIcon className="h-3.5 w-3.5" />
      <span className="font-semibold leading-none">{pinned ? `P${value}` : '—'}</span>
    </button>
  );
}
